import { recallConversation } from "./config.js";
import { mountLauncher, type Launcher } from "./launcher.js";

/**
 * The entry point the shop loads.
 *
 * Everything here is arranged around one question: what does a shopper pay who never
 * opens the chat? The answer is this file and `launcher.ts` — a few kilobytes that
 * draw a button. The React panel is a dynamic import, fetched the first time somebody
 * clicks, and never fetched at all for the overwhelming majority of page views.
 *
 * The one exception is a shopper who already has a conversation open. They moved
 * between product pages mid-chat, and the panel loads immediately so the reply that
 * arrives while they are reading a product description raises the unread badge. The
 * trade is deliberate: the extra fetch only happens for someone who has already
 * chosen to talk to us.
 */

/** Set once the panel module has been imported, so a second click does not refetch. */
let panel: { setOpen: (open: boolean) => void } | null = null;
let loading: Promise<void> | null = null;

async function loadPanel(launcher: Launcher): Promise<void> {
  // A single shared promise, so double-clicking the launcher imports once. Two
  // concurrent mounts would put two React roots in the same element and two sockets
  // on the same conversation.
  loading ??= (async () => {
    const module = await import("./panel/mount.js");
    panel = module.mountPanel(launcher);
  })();

  await loading;
}

function start(): void {
  const launcher = mountLauncher();

  launcher.onToggle((open) => {
    void loadPanel(launcher).then(() => {
      panel?.setOpen(open);
    });
  });

  // Mid-conversation across a page navigation. Load quietly, stay collapsed, and
  // connect — otherwise an assistant reply that takes half a minute arrives to a
  // page that is no longer listening.
  if (recallConversation() !== null) {
    void loadPanel(launcher);
  }
}

// `defer` on the script tag guarantees the document is parsed, but the plugin is not
// the only way this can be embedded and a tag manager may inject it at any moment.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
} else {
  start();
}
